/**
 * Intent → consent → CV upload → interview → profile review → first matches.
 * The product's first impression (PIVOT-044/082). The intent step (PIVOT-075)
 * is a low-friction "what brings you here" choice that rides along into the
 * interview's first turn.
 *
 * Two rules this page exists to hold (constitution §I, §VI):
 *
 * 1. **Never advance on a failure.** A failed finalize keeps the candidate on a
 *    retryable error rather than showing them a blank profile — which they would
 *    then save over the real one the server already built.
 * 2. **Never claim onboarding is done before it is.** The session is re-read
 *    from the server only after a save actually succeeded, so the route guard
 *    can't be waved through by a client-side flag.
 *
 * The constraints step (PIVOT-232) sits between the interview and finalize: five
 * factual questions the seven-turn interview never reaches, recorded before the
 * profile is built rather than hoped for afterwards.
 *
 * MATCHER-V2 ONBOARDING (PIVOT-299)
 * ----------------------------------
 * A v2 candidate (stamped at consent time, behind the `matcher_v2_onboarding`
 * flag — `POST /onboarding/consent`) takes the match-setup form
 * (`MatchSetupForm`) instead of the legacy constraints step, and takes one
 * extra step after reviewing their profile: approving the exact search policy
 * that form and the interview produced (`PolicyReview`). `contract` tracks
 * which of the two a candidate is on, learned once from
 * `GET /onboarding/match-setup` (the interview-done transition, or the entry
 * resolver below) and never re-derived afterwards for this session. A v1
 * candidate's flow is byte-for-byte what it was before this ticket.
 *
 * The last step calls cold start (PIVOT-081) behind a progress screen: that is
 * where spec §4/§8's "instant matches on signup" is delivered.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { ConsentGate } from '../components/ConsentGate'
import { ConstraintsStep } from '../components/ConstraintsStep'
import { CvUpload } from '../components/CvUpload'
import { IntentSelect } from '../components/IntentSelect'
import { InterviewChat } from '../components/InterviewChat'
import { MatchSetupForm } from '../components/MatchSetupForm'
import { OnboardingProgress } from '../components/OnboardingProgress'
import { OnboardingSteps } from '../components/OnboardingSteps'
import type { OnboardingStepKey } from '../components/OnboardingSteps'
import { PolicyReview } from '../components/PolicyReview'
import { ProfileReview } from '../components/ProfileReview'
import { Thinking } from '../components/Thinking'
import {
  finalizeOnboarding,
  getMatchSetup,
  getProfile,
  getSavedCv,
  hasSavedProfile,
  restartOnboarding,
  updateProfile,
} from '../api/onboarding'
import { coldStart } from '../api/matches'
import { useApiResource } from '../hooks/useApiResource'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import { recordFunnelStep, type FunnelStep } from '../lib/funnelEvents'
import type {
  CandidateEvidence,
  CareerBaseline,
  CareerBaselineCorrection,
  OnboardingContract,
  OnboardingIntent,
  Rubric,
  UserProfile,
} from '../types/domain'
import './Onboarding.css'

/** Every internal step this page can render is also one funnel event
 * (PIVOT-299 acceptance: "every onboarding step emits one funnel event") --
 * see the effect near the bottom of this component. */
type Step = FunnelStep

/** Which of the five markers each internal step sits under. */
const MARKER: Record<Step, OnboardingStepKey | null> = {
  intent: 'intent',
  consent: 'consent',
  declined: 'consent',
  upload: 'upload',
  interview: 'interview',
  // The match-setup form (PIVOT-299) and the constraints form (PIVOT-232) are
  // both the tail of the conversation, not steps the candidate chose from the
  // map -- same for finalize.
  'match-setup': 'interview',
  constraints: 'interview',
  finalizing: 'interview',
  review: 'review',
  // Approving the policy is still part of reviewing what onboarding produced.
  'policy-review': 'review',
  // The cold-start screen is its own full-bleed moment; a map on top of it is noise.
  scoring: null,
}

/** What the entry resolver decided: where to open, and which onboarding
 * contract this candidate is on (PIVOT-299) -- needed the moment the resumed
 * step is 'match-setup' or 'policy-review', where there is no separate signal
 * to fetch it from afterwards. */
type Entry =
  | { kind: 'ready'; step: Step; contract: OnboardingContract; requiresFinalize: boolean }
  | { kind: 'candidate-evidence-required' }

interface OnboardingProps {
  /** An explicit opt-in from Profile to review and approve matcher v2. */
  existingUserReview?: boolean
}

/**
 * Which step this candidate is actually on, decided on mount.
 *
 * Four signals, in order, and one that looks tempting but is a trap:
 *
 * - `profile_complete` — a finished candidate who is here came from the
 *   profile page's "redo the interview", which is a deliberate restart, so it
 *   starts at the top.
 * - `GET /profile` (`hasSavedProfile`) — a v2 candidate who finalized but left
 *   before approving their policy has a saved profile with `profile_complete`
 *   still `false` (v2 only flips that once a policy is active — `auth.py::
 *   _session_user`). Finalize already cleared their interview draft, so
 *   sending them back into `InterviewChat` would open a brand-new interview
 *   over nothing; they belong on `policy-review` directly. This can never be
 *   true for a v1 candidate: v1's `profile_complete` IS `has_rubric`, so a v1
 *   candidate with a saved profile would already have matched the branch above.
 * - `GET /onboarding/match-setup` — reports whether the interview is finished
 *   AND which contract this candidate is on (PIVOT-299). `interview_done` true
 *   with `contract: 'v2'` means match-setup is their next step; the same flag
 *   with `contract: 'v1'` means the legacy constraints step is. A candidate
 *   who closed the tab before answering either form still lands on the right
 *   one, and one who submitted it before a finalize that failed gets nothing
 *   asked twice.
 * - `GET /onboarding/cv` — a saved CV means consent was recorded and the
 *   upload step is behind them (the upload route is consent-gated, so a
 *   stored CV cannot exist without one). That is enough to resume at the
 *   interview: `InterviewChat` opens with `{message: null}`, which the
 *   backend answers by replaying the last turn when a draft interview exists,
 *   and by opening one over the stored CV when it does not.
 *
 * The trap: probing `POST /onboarding/interview` to *find out* whether an
 * interview is in progress. There is no read-only way to ask — a `null`
 * message with no stored state does not report "nothing here", it opens a
 * fresh interview with whatever `intent` the body carried, burning a model
 * call and fixing the session's intent to a default. So the CV is the probe,
 * and the interview call is left to `InterviewChat`, which has to make it
 * anyway.
 */
async function resolveEntry(
  profileComplete: boolean,
  existingUserReview: boolean,
  rebuildRequested: boolean,
  signal: AbortSignal,
): Promise<Entry> {
  if (profileComplete && existingUserReview) {
    const savedProfile = await getProfile(signal)
    if (savedProfile.candidate_evidence) {
      const setup = await getMatchSetup(signal)
      return {
        kind: 'ready',
        step: setup.answers ? 'policy-review' : 'match-setup',
        contract: 'v2',
        requiresFinalize: false,
      }
    }
    if (!rebuildRequested) return { kind: 'candidate-evidence-required' }

    const setup = await getMatchSetup(signal)
    if (setup.interview_done) {
      return { kind: 'ready', step: 'match-setup', contract: 'v2', requiresFinalize: true }
    }
    if (setup.interview_started) {
      return { kind: 'ready', step: 'interview', contract: 'v2', requiresFinalize: true }
    }
    return { kind: 'ready', step: 'intent', contract: 'v2', requiresFinalize: true }
  }
  if (profileComplete) {
    return { kind: 'ready', step: 'intent', contract: 'v1', requiresFinalize: true }
  }
  if (await hasSavedProfile(signal)) {
    return { kind: 'ready', step: 'policy-review', contract: 'v2', requiresFinalize: true }
  }
  const setup = await getMatchSetup(signal)
  if (setup.interview_done) {
    return setup.onboarding_contract === 'v2'
      ? { kind: 'ready', step: 'match-setup', contract: 'v2', requiresFinalize: true }
      : { kind: 'ready', step: 'constraints', contract: 'v1', requiresFinalize: true }
  }
  const saved = await getSavedCv(signal)
  return {
    kind: 'ready',
    step: saved ? 'interview' : 'intent',
    contract: setup.onboarding_contract,
    requiresFinalize: true,
  }
}

export default function Onboarding({ existingUserReview = false }: OnboardingProps) {
  const { t } = useI18n()
  const { user, refreshSession, signOut } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [step, setStep] = useState<Step | null>(null)
  const [contract, setContract] = useState<OnboardingContract>('v1')
  const [intent, setIntent] = useState<OnboardingIntent | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  // PIVOT-367 (A5). The review step needs both to show its baseline card: the
  // proposal to display, and the approved evidence a correction may point at.
  // Finalize returns them together, so they are read from its answer rather
  // than fetched a second time and risked disagreeing with the profile above.
  const [candidateEvidence, setCandidateEvidence] = useState<CandidateEvidence | null>(null)
  const [careerBaseline, setCareerBaseline] = useState<CareerBaseline | null>(null)
  // What the person confirmed or corrected on the review screen, held until
  // the act that commits it. A v2 candidate has no active policy yet, so
  // `PUT /profile` refuses the confirmation (A4) and the approval one step
  // later writes it in its own transaction. `undefined` is "this candidate
  // stated nothing about their career", which commits nothing.
  const [baselineConfirmation, setBaselineConfirmation] = useState<
    CareerBaselineCorrection | undefined
  >(undefined)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [requiresFinalize, setRequiresFinalize] = useState(true)
  const [upgradeStartError, setUpgradeStartError] = useState(false)

  const profileComplete = user?.profile_complete ?? false
  const rebuildRequested = existingUserReview && searchParams.get('rebuild') === '1'
  // `useApiResource` owns the cancellation: it aborts on unmount, which is the
  // whole reason this is not another hand-rolled effect (CLAUDE.md §7).
  const entry = useApiResource((signal) =>
    resolveEntry(profileComplete, existingUserReview, rebuildRequested, signal),
  )

  useEffect(() => {
    // One-way: the resolver decides where the flow *opens*, and after that the
    // candidate's own clicks own `step`. Guarding on `step === null` keeps a
    // late-arriving answer from yanking them back.
    //
    // A failed lookup is NOT waved through to 'intent' (PIVOT-299 review):
    // that used to be treated as a safe default, but it isn't one -- a v2
    // candidate who already finalized and merely hasn't approved yet would be
    // walked back through a brand-new interview instead of straight to
    // approval, with their already-built profile left behind. `entry.failed`
    // renders its own retry state below instead of guessing.
    if (step !== null || entry.loading || !entry.data || entry.data.kind !== 'ready') return
    setStep(entry.data.step)
    setContract(entry.data.contract)
    setRequiresFinalize(entry.data.requiresFinalize)
  }, [entry.loading, entry.data, step])

  // PIVOT-299 acceptance: "every onboarding step emits one funnel event."
  // Fires once per step the candidate actually reaches, not on every render.
  useEffect(() => {
    if (step !== null) recordFunnelStep(step)
  }, [step])

  const runFinalize = async (interviewAnswers: Record<string, string>) => {
    setStep('finalizing')
    setError(null)
    try {
      const result = await finalizeOnboarding(interviewAnswers, contract)
      setProfile(result.profile)
      setCandidateEvidence(result.candidate_evidence)
      setCareerBaseline(result.career_baseline)
      setStep('review')
    } catch {
      // Stay put with an honest error. Advancing into review with a blank
      // profile would let the next save wipe the one finalize may have already
      // persisted, and mark the user onboarded with nothing behind it.
      setError(t('onboarding.finalizeFailed'))
    }
  }

  const handleInterviewDone = (interviewAnswers: Record<string, string>) => {
    // The match-setup form (PIVOT-299) or the legacy constraints form
    // (PIVOT-232) sits here, between the interview and finalize: finalize is
    // what applies either one's answers, so they have to be recorded before
    // it runs — not after, when there would be nothing left to apply them to.
    setAnswers(interviewAnswers)
    void routeAfterInterview()
  }

  /** Which of the two forms comes next, learned from the one call that
   * carries the contract (see `resolveEntry`'s docstring).
   *
   * A failed lookup does NOT fall open to v1 (PIVOT-299 review): guessing
   * wrong here is unsafe in both directions. A mis-guessed v1 sends a real
   * v2 candidate through the legacy `ConstraintsStep`, whose finalize maps a
   * region straight into the legacy town field (R10-A7), and lets them reach
   * cold start having never approved a policy. The interview just finished,
   * so there is nothing to lose by retrying instead of guessing -- `error`
   * renders below the (still-mounted) interview with a button that calls
   * this again. */
  const routeAfterInterview = async () => {
    setError(null)
    if (existingUserReview) {
      setContract('v2')
      setStep('match-setup')
      return
    }
    try {
      const candidateContract = (await getMatchSetup()).onboarding_contract
      setContract(candidateContract)
      setStep(candidateContract === 'v2' ? 'match-setup' : 'constraints')
    } catch {
      setError(t('onboarding.routeAfterInterviewFailed'))
    }
  }

  /** The tail both `review` (v1) and `policy-review` (v2) share: score the
   * first matches, then re-read the session so the route guard sees the
   * truth rather than a client-set flag. `retryStep` is where a failed
   * re-read sends the candidate back to — the step whose own retry control
   * re-runs the save that got them here. */
  const proceedToScoring = async (retryStep: Step) => {
    setStep('scoring')

    // Cold start is best-effort: the profile is saved either way, and the
    // daily run scores whatever this misses. Don't trap the user in
    // onboarding for it. It runs ahead of the session re-read so that losing
    // the re-read does not also cost the candidate the first matches this
    // step is here to buy.
    await coldStart().catch(() => undefined)

    // The profile is real and stored; ask the server to re-read the session so
    // `RequireOnboarded` sees the truth rather than a flag we set ourselves.
    //
    // Trap: swallowing this failure is not "the guard settles on the next load".
    // Nothing in-app re-reads the session, so `/dashboard` is entered with the
    // stale `profile_complete: false` this page mounted with, `RequireOnboarded`
    // bounces back to `/onboarding`, and — the draft having been cleared by
    // finalize — the candidate is walked into a brand-new interview, locked out
    // of every onboarded route until they reload by hand. Fail loud instead
    // (constitution §VI): back to `retryStep`, where the finish/approve button
    // re-runs this same tail with the same already-saved state.
    try {
      await refreshSession()
    } catch {
      setStep(retryStep)
      setError(t('common.error'))
      return
    }
    void navigate('/dashboard')
  }

  const handleSave = async (
    edited: UserProfile,
    _rubric?: Rubric,
    editedBaseline?: CareerBaselineCorrection,
  ) => {
    setError(null)
    try {
      // PIVOT-367 (A4): a v2 candidate's baseline confirmation is NOT sent
      // here. It commits with the policy approval one step later, in that
      // call's own transaction, so a failed approval leaves no confirmed
      // career fact on an account with no approved policy. A v1 candidate
      // never approves a policy, so this save is their commit point.
      await updateProfile(edited, undefined, contract === 'v2' ? undefined : editedBaseline)
    } catch {
      setError(t('onboarding.saveFailed'))
      return
    }
    // Carry the edits the server now holds. The fail-loud path below remounts
    // `ProfileReview`, which seeds its form from this prop, so leaving `profile`
    // on finalize's output would show the candidate their pre-edit text and
    // re-PUT it over the version they just saved.
    setProfile(edited)

    if (contract === 'v2') {
      setBaselineConfirmation(editedBaseline)
      // One more step before scoring: approve the exact policy the interview
      // and match-setup form produced.
      setStep('policy-review')
      return
    }
    await proceedToScoring('review')
  }

  const handlePolicyApproved = () => {
    void proceedToScoring('policy-review')
  }

  /** Clear only the abandoned interview draft, then keep the explicit v2
   * rebuild in the URL so a hard refresh cannot fall back to the v1 entry. */
  const handleUpgradeRebuild = async () => {
    setUpgradeStartError(false)
    try {
      await restartOnboarding()
    } catch {
      setUpgradeStartError(true)
      return
    }
    setSearchParams({ rebuild: '1' }, { replace: true })
    setRequiresFinalize(true)
    setContract('v2')
    setStep('intent')
  }

  // Nothing is rendered until the entry step is known. Painting the intent
  // question and then swapping it for a half-finished interview is the flash
  // this resolution exists to avoid (CLAUDE.md §7); it is an ordinary indexed
  // read, so it gets the plain loading line rather than `Thinking` (§9).
  if (step === null) {
    return (
      <div className="onboarding-page">
        <title>{`${t('onboarding.pageTitle')} — ${t('common.appName')}`}</title>
        {entry.data?.kind === 'candidate-evidence-required' ? (
          <div className="stack">
            <h2>{t('onboarding.upgradeEvidenceTitle')}</h2>
            <p>{t('onboarding.upgradeEvidenceBody')}</p>
            {upgradeStartError && (
              <p className="form-error" role="alert">
                {t('profile.redoFailed')}
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleUpgradeRebuild()}
            >
              {t('onboarding.upgradeEvidenceAction')}
            </button>
          </div>
        ) : entry.failed ? (
          // Never advance on a failure (this page's own rule): guessing
          // 'intent' here used to be the fallback, but it isn't a safe one --
          // see the resolution effect's own comment. Retry instead.
          <div className="stack">
            <p className="form-error" role="alert">
              {t('onboarding.entryLoadFailed')}
            </p>
            <button type="button" className="btn btn-primary" onClick={() => void entry.reload()}>
              {t('onboarding.retry')}
            </button>
          </div>
        ) : (
          <p role="status">{t('common.loading')}</p>
        )}
      </div>
    )
  }

  const marker = MARKER[step]

  return (
    <div className="onboarding-page">
      <title>{`${t('onboarding.pageTitle')} — ${t('common.appName')}`}</title>
      {/* `.page-header` (styles/layout.css, PIVOT-128): the progress map moves
          into the shared header pattern instead of floating loose above the
          step content. There is no title/action pair here — one child is a
          normal, supported use of the primitive. */}
      {marker && (
        <div className="page-header">
          <OnboardingSteps current={marker} showBackLink={profileComplete} />
        </div>
      )}

      {/* `key={step}` remounts this wrapper on every step change, which is
          what gives it a fresh entrance to animate (PIVOT-143) — the map
          above never remounts between steps, so its own stagger only ever
          played once, on the step content that changes every time. One
          orchestrated moment per step, not scattered fizz — see the
          `onboarding-step-in` keyframe in Onboarding.css for the reduced-
          motion guard. */}
      <div className="onboarding-step-body" key={step}>
        {step === 'intent' && (
          <IntentSelect
            onSelect={(selected) => {
              setIntent(selected)
              setStep('consent')
            }}
          />
        )}
        {step === 'consent' && (
          <ConsentGate onAccept={() => setStep('upload')} onDecline={() => setStep('declined')} />
        )}
        {step === 'declined' && (
          <div className="stack">
            <h2>{t('onboarding.declinedTitle')}</h2>
            <p>{t('onboarding.declinedBody')}</p>
            <div className="onboarding-declined-actions">
              <button type="button" className="btn btn-primary" onClick={() => setStep('consent')}>
                {t('onboarding.declinedBack')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  signOut()
                  void navigate('/', { replace: true })
                }}
              >
                {t('common.signOut')}
              </button>
            </div>
          </div>
        )}
        {step === 'upload' && <CvUpload onReady={() => setStep('interview')} />}
        {step === 'interview' && (
          <>
            {/* `intent` is null on a resumed interview — the choice was made
                in a previous session and lives on the server's stored
                session, so this sends none rather than inventing a
                replacement (see the prop's docstring in `InterviewChat`). */}
            <InterviewChat intent={intent ?? undefined} onDone={handleInterviewDone} />
            {/* `routeAfterInterview`'s own failure (PIVOT-299 review): the
                interview itself already finished, so this renders below it
                rather than replacing it -- retrying re-reads the contract,
                it does not restart the conversation. */}
            {error && (
              <div className="stack">
                <p className="form-error" role="alert">
                  {error}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void routeAfterInterview()}
                >
                  {t('onboarding.retry')}
                </button>
              </div>
            )}
          </>
        )}
        {step === 'match-setup' && (
          // `answers` is `{}` on a resumed flow — the interview happened in a
          // previous session and finalize rebuilds the real answers from the
          // server's own stored transcript, never from this copy.
          <MatchSetupForm
            onSaved={() => {
              if (requiresFinalize) {
                void runFinalize(answers)
                return
              }
              setStep('policy-review')
            }}
          />
        )}
        {step === 'constraints' && (
          <ConstraintsStep onSaved={() => void runFinalize(answers)} />
        )}
        {step === 'finalizing' && (
          <div className="stack">
            {error ? (
              <>
                <p className="form-error" role="alert">
                  {error}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void runFinalize(answers)}
                >
                  {t('onboarding.retry')}
                </button>
              </>
            ) : (
              <Thinking
                wide
                label="onboarding.profileBuilding"
                patience="onboarding.profileBuildingPatience"
              />
            )}
          </div>
        )}
        {step === 'review' && profile && (
          <>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <ProfileReview
              profile={profile}
              // PIVOT-367: without both of these the baseline card is not
              // rendered at all, and onboarding is the one screen where the
              // person first sees the job we think they hold.
              careerBaseline={careerBaseline}
              candidateEvidence={candidateEvidence}
              onSave={handleSave}
              submitLabel="onboarding.reviewFinish"
              heading="onboarding.reviewTitle"
              body="onboarding.reviewBody"
            />
          </>
        )}
        {step === 'policy-review' && (
          <>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <PolicyReview
              onApproved={handlePolicyApproved}
              careerBaseline={baselineConfirmation}
            />
          </>
        )}
        {step === 'scoring' && <OnboardingProgress />}
      </div>
    </div>
  )
}
